import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Box,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { Business } from '../../services/clientService';

interface MastersTableProps {
  masters: Business[];
  favoriteMasters: Business[];
  onAddFavorite: (masterId: string) => void;
  onRemoveFavorite: (masterId: string) => void;
  onShowWorkingHours?: (master: Business) => void;
  language: string;
}

const MastersTable: React.FC<MastersTableProps> = ({
  masters,
  favoriteMasters,
  onAddFavorite,
  onRemoveFavorite,
  onShowWorkingHours,
  language,
}) => {
  const isFavorite = (masterId: string) => {
    return (favoriteMasters || []).some(fav => fav.id === masterId);
  };

  const handleFavoriteClick = (masterId: string) => {
    if (isFavorite(masterId)) {
      onRemoveFavorite(masterId);
    } else {
      onAddFavorite(masterId);
    }
  };

  const formatAddress = (city?: string, street?: string, building?: string) => {
    const parts = [city, street, building].filter(Boolean);
    return parts.join(', ') || (language === 'ua' ? 'Адреса не вказана' : 'Address not specified');
  };

  const formatContacts = (phone?: string, email?: string) => {
    const contacts = [];
    if (phone) contacts.push(phone);
    if (email) contacts.push(email);
    return contacts.join(', ') || (language === 'ua' ? 'Контакти не вказані' : 'Contacts not specified');
  };

  if (!masters || masters.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <div style={{ textAlign: 'center' }}>
          <i className="bi bi-people" style={{ fontSize: '3rem', color: '#ccc' }}></i>
          <h3>{language === 'ua' ? 'Немає доступних майстрів' : 'No masters available'}</h3>
          <p>{language === 'ua' ? 'Наразі немає майстрів у вашому місті' : 'No masters are currently available in your city'}</p>
        </div>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
              {language === 'ua' ? 'Фото' : 'Photo'}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
              {language === 'ua' ? 'Назва' : 'Name'}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
              {language === 'ua' ? 'Контакти' : 'Contacts'}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>
              {language === 'ua' ? 'Адреса' : 'Address'}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
              {language === 'ua' ? 'Категорії' : 'Categories'}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
              {language === 'ua' ? 'Послуги' : 'Services'}
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
              {language === 'ua' ? 'Дії' : 'Actions'}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {masters.map((master) => (
            <TableRow key={master.id} hover>
              <TableCell>
                <Avatar
                  src={master.logo_url}
                  alt={master.name}
                  sx={{ width: 50, height: 50 }}
                >
                  {master.name.charAt(0).toUpperCase()}
                </Avatar>
              </TableCell>
              <TableCell>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{master.name}</div>
                  {master.rating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem', color: '#666' }}>
                      <span style={{ color: '#ffc107' }}>★</span>
                      <span>{master.rating.toFixed(1)}</span>
                      {master.reviews_count && (
                        <span>({master.reviews_count} {language === 'ua' ? 'відгуків' : 'reviews'})</span>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {formatContacts(master.phone, master.email)}
              </TableCell>
              <TableCell>
                {formatAddress(master.city, master.street, master.building)}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {master.categories && master.categories.length > 0 ? (
                    master.categories.slice(0, 2).map((category, index) => (
                      <Chip
                        key={index}
                        label={category}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))
                  ) : (
                    <span style={{ color: '#999', fontSize: '0.875rem' }}>
                      {language === 'ua' ? 'Не вказано' : 'Not specified'}
                    </span>
                  )}
                  {master.categories && master.categories.length > 2 && (
                    <Chip
                      label={`+${master.categories.length - 2}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {master.services && master.services.length > 0 ? (
                    master.services.slice(0, 2).map((service, index) => (
                      <Chip
                        key={index}
                        label={service}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))
                  ) : (
                    <span style={{ color: '#999', fontSize: '0.875rem' }}>
                      {language === 'ua' ? 'Не вказано' : 'Not specified'}
                    </span>
                  )}
                  {master.services && master.services.length > 2 && (
                    <Chip
                      label={`+${master.services.length - 2}`}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {onShowWorkingHours && (
                    <IconButton
                      onClick={() => onShowWorkingHours(master)}
                      color="primary"
                      title={language === 'ua' ? 'Переглянути години роботи' : 'View working hours'}
                    >
                      <AccessTimeIcon />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => handleFavoriteClick(master.id)}
                    color={isFavorite(master.id) ? 'error' : 'default'}
                    title={
                      isFavorite(master.id)
                        ? (language === 'ua' ? 'Видалити з улюблених' : 'Remove from favorites')
                        : (language === 'ua' ? 'Додати до улюблених' : 'Add to favorites')
                    }
                  >
                    {isFavorite(master.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MastersTable;
